import { GoogleOutlined } from '@ant-design/icons'
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google'
import { App, Button, Space, Tooltip } from 'antd'
import { captureException } from '@tucan/utils/error-tracking'
import debug from 'debug'
import { useApiProxy } from 'hooks/use.api.proxy'
import { FC, useState, JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from 'store'
import { triggerLogin } from 'store/auth'
import { useRegisterActivity } from '../../hooks/use.register.activity'
import { getPublicEnv } from '@tucan/utils'

const logger = debug('auth:LoginWithGoogleButton')

interface Props {
  onSubmitting?: (submitting: boolean) => void
  onSuccess?: () => void
  disabled?: boolean
  onClick?: (cb: () => void) => void
}

const ActualButton: FC<Props> = props => {

  const { t } = useTranslation()
  const authenticated = useAppSelector(state => state.auth.authenticated)
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const registerActivity = useRegisterActivity()
  const [errorTitle, setErrorTite] = useState<string>()
  const { trigger } = useApiProxy('/auth/google')

  const login = useGoogleLogin({
    flow: 'auth-code',
    onError: (e) => {
      logger('callback failure %O', e)

      captureException(e)
      void message.warning({
        content: e.error_description || t('can-not-login-with-google', 'Cannot use Google Login'),
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onSuccess: async tokenResponse => {
      try {
        props.onSubmitting && props.onSubmitting(true)

        const result = await trigger({
          data: {
            code: tokenResponse.code,
          },
          method: 'POST',
        })

        if (result.login) {
          void message.success(t('login-successfully', 'You are now logged in!'))
          dispatch(triggerLogin())
          void registerActivity('LOGIN', undefined, {
            with: 'google',
          })
          props.onSuccess && props.onSuccess()
        } else {
          logger('invalid login response %O', result)
          void message.error(t('google-login-failed', 'Login with google failed'))
          captureException(result)
        }
      } catch (e) {
        logger('login failed %O', e)
        void message.error(t('google-login-failed', 'Login with google failed'))
        captureException(e)
      }


      props.onSubmitting && props.onSubmitting(false)
    },
  })

  const wrap = (element: JSX.Element): JSX.Element => {
    if (errorTitle) {
      return (
        <Tooltip title={errorTitle}>
          {element}
        </Tooltip>
      )
    }

    return element
  }

  return (
    <Button
      size="large"
      block
      onClick={props.onClick ? () => props.onClick(login) : () => login()}
      disabled={props.disabled || authenticated}
    >
      <Space>
        <GoogleOutlined />
        <span>{t('login-google', 'Login with Google')}</span>
      </Space>
    </Button>
  )
}

export const LoginWithGoogleButton: FC<Props> = props => {
  const [scriptError, setScriptError] = useState<boolean>(false)

  if (scriptError || !getPublicEnv('GOOGLE_CLIENT_ID')) {
    return null
  }

  return (
    <GoogleOAuthProvider
      clientId={getPublicEnv('GOOGLE_CLIENT_ID')}
      onScriptLoadError={() => {
        setScriptError(true)
      }}
    >
      <ActualButton {...props} />
    </GoogleOAuthProvider>
  )
}
